import {
  OperationLifecycleState,
  OperationProgressItemState,
  type OperationProgressSnapshot,
} from './gen/revit_service_pb';
import { resolveSnapshotProgressVisibility } from './operationProgressVisibility';

export type RevitOperationLifecycleState =
  | 'unspecified'
  | 'queued'
  | 'waiting_for_revit'
  | 'running'
  | 'waiting_for_user_input'
  | 'completing'
  | 'completed'
  | 'failed';

export type RevitOperationProgressItemStatus =
  | 'pending'
  | 'running'
  | 'complete'
  | 'skipped'
  | 'conflict'
  | 'error';

export type RevitOperationProgressItem = {
  readonly id: string;
  readonly label: string;
  readonly detail?: string;
  readonly status: RevitOperationProgressItemStatus;
  readonly error?: string;
  readonly sequence: number;
};

export type RevitOperationProgressSnapshot = {
  operationId: string;
  operationType: string;
  phase: string;
  state?: RevitOperationLifecycleState;
  functionName: string;
  message: string;
  detail?: string;
  currentItem?: string;
  waitReason?: string;
  waitElapsedMs?: number;
  activeElapsedMs?: number;
  processed?: number;
  total?: number;
  percent?: number | null;
  elapsedMs?: number;
  etaMs?: number | null;
  cacheStatus?: string;
  issuesFound?: number;
  resultReady: boolean;
  failed: boolean;
  error?: string;
  items: RevitOperationProgressItem[];
};

const mapOperationLifecycleState = (
  state: OperationLifecycleState | undefined
): RevitOperationLifecycleState => {
  switch (state) {
    case OperationLifecycleState.QUEUED:
      return 'queued';
    case OperationLifecycleState.WAITING_FOR_REVIT:
      return 'waiting_for_revit';
    case OperationLifecycleState.RUNNING:
      return 'running';
    case OperationLifecycleState.WAITING_FOR_USER_INPUT:
      return 'waiting_for_user_input';
    case OperationLifecycleState.COMPLETING:
      return 'completing';
    case OperationLifecycleState.COMPLETED:
      return 'completed';
    case OperationLifecycleState.FAILED:
      return 'failed';
    case OperationLifecycleState.UNSPECIFIED:
    default:
      return 'unspecified';
  }
};

const mapOperationProgressItemState = (
  state: OperationProgressItemState
): RevitOperationProgressItemStatus => {
  switch (state) {
    case OperationProgressItemState.RUNNING:
      return 'running';
    case OperationProgressItemState.COMPLETED:
      return 'complete';
    case OperationProgressItemState.SKIPPED:
      return 'skipped';
    case OperationProgressItemState.CONFLICT:
      return 'conflict';
    case OperationProgressItemState.FAILED:
      return 'error';
    case OperationProgressItemState.UNSPECIFIED:
    default:
      return 'pending';
  }
};

export const mapOperationProgressSnapshot = (
  progress: OperationProgressSnapshot
): RevitOperationProgressSnapshot => {
  const state = mapOperationLifecycleState(progress.state);
  const visibility = resolveSnapshotProgressVisibility({
    state,
    percent: progress.total > 0 || progress.resultReady ? progress.percent : undefined,
    total: progress.total,
    etaMs: Number(progress.etaMs),
  });

  return {
    operationId: progress.operationId,
    operationType: progress.operationType,
    phase: progress.phase,
    state,
    functionName: progress.functionName,
    message: progress.message,
    detail: progress.detail || undefined,
    currentItem: progress.currentItem || undefined,
    waitReason: progress.waitReason || undefined,
    waitElapsedMs: Number(progress.waitElapsedMs),
    activeElapsedMs: Number(progress.activeElapsedMs),
    processed: progress.processed,
    total: progress.total,
    percent: visibility.percent,
    elapsedMs: Number(progress.elapsedMs),
    etaMs: visibility.etaMs,
    cacheStatus: progress.cacheStatus || undefined,
    issuesFound: progress.issuesFound,
    resultReady: progress.resultReady,
    failed: progress.failed,
    error: progress.error || undefined,
    items: [...(progress.items ?? [])]
      .sort((left, right) => left.sequence - right.sequence)
      .map((item) => ({
        id: item.itemId,
        label: item.label || item.itemId,
        detail: item.detail || undefined,
        status: mapOperationProgressItemState(item.state),
        error: item.error || undefined,
        sequence: item.sequence,
      })),
  };
};
